import { ResponseType } from 'axios';
import { ONBOARDING_CURRENCY_TYPE, SOLICITOR_NOMINEE } from 'src/constants';
import { ENDPOINT, HTTP_METHOD } from 'src/constants/api';
import {
	IAccountDetail,
	IAddress,
	IBankAccount,
	IBorrowerEmploymentDetail,
	IBorrowerFinancialDetail,
	IEmploymentDetail,
	IFunderSupportingDocument,
	ILoanLendingCriteria,
	IOtherDetail,
	IPersonalDetails
} from 'src/interfaces/accounts/account';
import { DTO } from './base.dto';

export interface ICreateFunderAccountRequest extends IPersonalDetails {
	residentialAddress: IAddress;
	mailingAddress?: IAddress;
	dateOfBirth?: Date;
	employmentDetail?: IEmploymentDetail
}

export interface CreateFunderAccountDetailResponse {
	personId?: string;
	residentialAddressId?: string;
	mailingAddressId?: string;
}

export class AccountDetailDTO extends DTO {
	public param: object | undefined;
	public query: undefined;
	public body: ICreateFunderAccountRequest;
	public url: string = ENDPOINT.FUNDER_ACCOUNT_DETAIL;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: ICreateFunderAccountRequest) {
		super();
		this.body = body;
	}
}

export interface IFunderOnboardingProgress {
	accountDetail?: IAccountDetail;
	employmentOnboardingDetail?: IEmploymentDetail;
	loanLendingCriteriaOnboarding?: ILoanLendingCriteria;
	bankAccountOnboarding?: IBankAccount;
	supportingDocuments?: IFunderSupportingDocument;
	referenceCurrency?: ONBOARDING_CURRENCY_TYPE;
	submittedToKyc?: boolean;
}
export interface IBorrowerOnboardingProgressResponse {
	accountDetail?: IAccountDetail;
	borrowerEmploymentDetailResponse?: IBorrowerEmploymentDetail;
	borrowerFinancialDetailResponse: IBorrowerFinancialDetail;
	otherDetailResponse?: IOtherDetail;
	supportingDocuments?: IFunderSupportingDocument;
	referenceCurrency?: ONBOARDING_CURRENCY_TYPE;
	submittedToKyc?: boolean;
}

export interface IFunderFSA {
	fullName?: string;
	generateDate?: Date;
	country?: string;
	hkIdPart1?: string;
	hkId?: string;
	hkIdPart2?: string;
	chineseIdNo?: string;
	passportNo?: string;
	address?: {
		id?: string;
		addressLine?: string;
		street?: string;
		district?: string;
		country?: string;
		zipCode?: string;
		livedTime?: number;
	};
	solicitorNominee?: SOLICITOR_NOMINEE;
	onboardingStatus?: number;
}
export class FunderOnboardingProgressDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.INDIVIDUAL_FUNDER_ONBOARDING_PROGRESS;
	public method: HTTP_METHOD = HTTP_METHOD.GET;
	public readonly responseType: ResponseType = 'json';
	constructor() {
		super();
	}
}

export interface ISubmitFsaFileParam {
	personId?: string;
}

export class IndividualFunderOnboardingFsaDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.INDIVIDUAL_FUNDER_FSA;
	public method: HTTP_METHOD = HTTP_METHOD.GET;
	public readonly responseType: ResponseType = 'json';
	constructor() {
		super();
	}
}

export class IndividualSignFsaDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.INDIVIDUAL_FUNDER_SIGN_FSA;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor() {
		super();
	}
}

export class EmploymentDetailOnboardingDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: IEmploymentDetail;
	public url: string = ENDPOINT.FUNDER_EMPLOYMENT_DETAIL;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: IEmploymentDetail) {
		super();
		this.body = body;
	}
}

export class LoanLendingDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: ILoanLendingCriteria;
	public url: string = ENDPOINT.FUNDER_LOAN_LENDING;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: ILoanLendingCriteria) {
		super();
		this.body = body;
	}
}

export class BankAccountDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: IBankAccount;
	public url: string = ENDPOINT.FUNDER_BANK_ACCOUNT;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: IBankAccount) {
		super();
		this.body = body;
	}
}

export class DeleteFileDTO extends DTO {
	public param: { documentId: string };
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.DELETE_FUNDER_FILE;
	public method: HTTP_METHOD = HTTP_METHOD.DELETE;
	public readonly responseType: ResponseType = 'json';
	constructor(param: { documentId: string }) {
		super();
		this.param = param;
	}
}

export class SaveDraftDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.FUNDER_ONBOARDING_DAVE_DRAFT;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(url = ENDPOINT.FUNDER_ONBOARDING_DAVE_DRAFT) {
		super();
		if (url) {
			this.url = url;
		}
	}
}

export class SubmitDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.FUNDER_ONBOARDING_SUBMIT;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(url = ENDPOINT.FUNDER_ONBOARDING_SUBMIT) {
		super();
		if (url) {
			this.url = url;
		}
	}
}

export class BorrowerOnboardingProgressDTO extends FunderOnboardingProgressDTO {
	public url: string = ENDPOINT.INDIVIDUAL_BORROWER_ONBOARDING_PROGRESS;
	constructor() {
		super();
	}
}

export class BorrowerAccountDTO extends AccountDetailDTO {
	public url: string = ENDPOINT.BORROWER_ACCOUNT_DETAIL;
	constructor(body: ICreateFunderAccountRequest) {
		super(body);
	}
}

export interface IBorrowerFinancialResponse {
	personId?: string;
	financialYearlyIncomeId?: string;
	financialRegularExpenditureId?: string;
	financialRealEstateId?: string;
	personalBalanceSheetId?: string;
}
export class BorrowerFinancialDetailDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: IBorrowerFinancialDetail;
	public url: string = ENDPOINT.BORROWER_FINANCIAL_DETAIL;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: IBorrowerFinancialDetail) {
		super();
		this.body = body;
	}
}

export class BorrowerEmploymentDetailOnboardingDTO extends EmploymentDetailOnboardingDTO {
	public body: IBorrowerEmploymentDetail;
	public url: string = ENDPOINT.BORROWER_EMPLOYMENT_DETAIL;
	constructor(body: IBorrowerEmploymentDetail) {
		super(body);
		this.body = body;
	}
}

export class OtherDetailDTO extends DTO {
	public param: undefined;
	public query: undefined;
	public body: IOtherDetail;
	public url: string = ENDPOINT.BORROWER_OTHER_DETAIL;
	public method: HTTP_METHOD = HTTP_METHOD.POST;
	public readonly responseType: ResponseType = 'json';
	constructor(body: IOtherDetail) {
		super();
		this.body = body;
	}
}

export interface ICreateFunderCorporateRequest extends IPersonalDetails {
	residentialAddress: IAddress;
	mailingAddress?: IAddress;
	dateOfBirth?: Date;
	referenceCurrency?: string;
	companyInfoId?: string;
}

export class CorporateBankAccountDTO extends BankAccountDTO {
	public url: string = ENDPOINT.CORPORATE_FUNDER_BANK_ACCOUNT;

	constructor(body: IBankAccount) {
		super(body);
	}
}

export class ResubmitRequestDTO extends DTO {
	public param: { onboardingRequestId: string };
	public query: undefined;
	public body: undefined;
	public url: string = ENDPOINT.RESUBMIT_REQUEST;
	public method: HTTP_METHOD = HTTP_METHOD.PUT;
	public readonly responseType: ResponseType = 'json';
	constructor(param: { onboardingRequestId: string }) {
		super();
		this.param = param;
	}
}
